import { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import type { AccountInfo } from '@azure/msal-browser';
import EmployeeService from '../services/EmployeeService';

const OID_CLAIM_URI = 'http://schemas.microsoft.com/identity/claims/objectidentifier';

const extractObjectId = (account?: AccountInfo | null): string | undefined => {
  if (!account) {
    return undefined;
  }

  const { idTokenClaims } = account;
  const directOid = idTokenClaims?.oid;
  if (typeof directOid === 'string' && directOid.length > 0) {
    return directOid;
  }

  const claimMap = idTokenClaims as Record<string, unknown> | undefined;
  const uriClaim = claimMap?.[OID_CLAIM_URI];
  if (typeof uriClaim === 'string' && uriClaim.length > 0) {
    return uriClaim;
  }

  return undefined;
};

// oid → the backend-RESOLVED employee id (Build C, plan field-members-and-signin-conversion):
// a CONVERTED field member's token oid maps to their original employeeid via entra_oid, and
// every consumer of this hook treats its value as the employee identity. check_user is the
// resolver; for today's users (and any failure) it answers the oid itself, so the swap is a
// no-op re-render for everyone unconverted. Module-level cache: one request per oid per
// session, shared by every mounted consumer.
const resolvedIds = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

const resolveEmployeeId = (oid: string): Promise<string> => {
  const cached = resolvedIds.get(oid);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }
  let pending = inflight.get(oid);
  if (!pending) {
    // Promise.resolve().then(...) so a resolver that THROWS synchronously (a consumer's
    // partial EmployeeService mock, an import-order accident) degrades to the raw oid
    // through the same .catch as a rejection — the hook must never take a consumer down.
    pending = Promise.resolve()
      .then(() => EmployeeService.checkUser(oid))
      .then((r) => {
        // Cache SUCCESS only: a transient failure at app load must not pin a converted
        // member's whole session to the raw oid — the next mount retries.
        const id = r.exists && r.employeeId ? r.employeeId : oid;
        resolvedIds.set(oid, id);
        return id;
      })
      .catch(() => oid)
      .then((id) => {
        inflight.delete(oid);
        return id;
      });
    inflight.set(oid, pending);
  }
  return pending;
};

/** Test seam: a converted identity cached in one test must not leak into the next. */
export const __clearResolvedEmployeeIds = (): void => {
  resolvedIds.clear();
  inflight.clear();
};

// ON by default (the app always resolves); setupTests turns it OFF for the whole jest run
// because a consumer suite that doesn't mock EmployeeService would otherwise fire a REAL
// checkUser from this effect — axios → MSAL popup → jsdom's unimplemented window.open —
// whose chain logs after the suite ends and fails CI with every test green. Disabled, the
// hook is exactly its pre-resolution self (the raw oid), which is what consumer suites
// assert; the hook's own tests opt back in. A GLOBAL flag, deliberately: setupTests must
// not import this module (that would load the real EmployeeService into every suite's
// registry ahead of its hoisted jest.mock).
const resolutionDisabled = (): boolean =>
  Boolean((globalThis as { __EMPLOYEE_ID_RESOLUTION_DISABLED__?: boolean })
    .__EMPLOYEE_ID_RESOLUTION_DISABLED__);

/** Test seam: see above — the hook's own tests re-enable, everyone else stays inert. */
export const __setEmployeeIdResolution = (enabled: boolean): void => {
  (globalThis as { __EMPLOYEE_ID_RESOLUTION_DISABLED__?: boolean })
    .__EMPLOYEE_ID_RESOLUTION_DISABLED__ = !enabled;
};

/**
 * The active account's EMPLOYEE id. Synchronously the MSAL oid (today's users' identity, and
 * first-session self-onboarding's), swapping to the backend-resolved employeeid once
 * check_user answers — the only case where they differ is a converted field member, whose
 * history lives under the original id.
 */
const useActiveEmployeeId = (): string | undefined => {
  const { instance, accounts } = useMsal();

  const oid = useMemo(() => {
    const activeAccount = instance.getActiveAccount?.() ?? accounts[0];
    return extractObjectId(activeAccount ?? undefined);
  }, [accounts, instance]);

  // The state carries WHICH oid it resolved (pre-PR review): an in-session account switch
  // A→B must never return A's resolved id under B's oid — unmatched state is ignored and
  // the module cache answers synchronously for any oid it already knows.
  const [resolved, setResolved] = useState<{ oid: string; id: string } | undefined>(() =>
    (oid !== undefined && resolvedIds.has(oid)
      ? { oid, id: resolvedIds.get(oid) as string }
      : undefined),
  );

  useEffect(() => {
    if (!oid || resolutionDisabled()) {
      setResolved(undefined);
      return undefined;
    }
    let isMounted = true;
    resolveEmployeeId(oid).then((id) => {
      if (isMounted) {
        setResolved({ oid, id });
      }
    });
    return () => {
      isMounted = false;
    };
  }, [oid]);

  if (!oid) {
    return undefined;
  }
  // Raw-oid-first: existing users and fresh sessions keep today's synchronous timing; the
  // value flips only for a converted member, once.
  if (resolved && resolved.oid === oid) {
    return resolved.id;
  }
  return resolvedIds.get(oid) ?? oid;
};

export default useActiveEmployeeId;
